import { getTripReference } from "./tripReference.util";

const hasInspectionValue = value => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
};

const firstInspectionValue = (...values) => {
    const value = values.find(hasInspectionValue);
    return value === undefined ? "" : value;
};

const unwrapInspectionRecord = inspect => {
    if (!inspect) return inspect;

    return inspect?.data ?? inspect?.inspection ?? inspect?.preInspection ?? inspect?.postInspection ?? inspect;
};

const resolvePersonName = person => {
    if (!person) return "";
    if (typeof person === "string") return person.trim();

    return firstInspectionValue(
        [person?.name, person?.surname || person?.lastName].filter(hasInspectionValue).join(" ").trim(),
        person?.displayName,
        person?.fullName,
        person?.name
    );
};

export const resolveInspectionDriverName = (inspect, trip = null, fallbackInspection = null) => {
    const record = unwrapInspectionRecord(inspect);
    const fallback = unwrapInspectionRecord(fallbackInspection);

    return firstInspectionValue(
        resolvePersonName(record?.driver?.user),
        record?.driverName,
        resolvePersonName(record?.driver),
        record?.driver?.user?.email,
        resolvePersonName(fallback?.driver?.user),
        fallback?.driverName,
        resolvePersonName(fallback?.driver),
        fallback?.driver?.user?.email,
        trip?.driverName,
        resolvePersonName(trip?.driver?.user),
        resolvePersonName(trip?.driver),
        trip?.driver?.user?.email
    );
};

const normalizeInspectionImage = image => {
    if (!image) return null;

    if (typeof image === "string") {
        return {
            pic: image,
            image,
        };
    }

    const uri = image?.image || image?.url || image?.uri || image?.pic || "";

    return {
        ...image,
        pic: uri,
        image: uri,
    };
};

const inspectionImageGroupToArray = group => {
    if (!hasInspectionValue(group)) return [];
    if (Array.isArray(group)) return group;
    if (typeof group === "string") return [group];
    if (typeof group === "object") return Object.values(group).filter(hasInspectionValue);

    return [];
};

const normalizeInspectionImageGroup = (...groups) => {
    const images = groups
        .map(inspectionImageGroupToArray)
        .find(group => group.length);
    if (!Array.isArray(images)) return [];

    return images
        .map(normalizeInspectionImage)
        .filter(image => image?.pic || image?.image);
};

const pickImageGroup = (inspect, key, ...aliases) => {
    const fields = [key, ...aliases];
    const containers = ["images", "photos", "vehicleImages", "vehiclePhotos"];
    const directValues = fields.map(field => inspect?.[field]);
    const nestedValues = containers.flatMap(container => fields.map(field => inspect?.[container]?.[field]));

    return normalizeInspectionImageGroup(
        ...directValues,
        ...nestedValues
    );
};

const padDatePart = value => String(value).padStart(2, "0");

const parseDateParts = (year, month, day, time = {}) => {
    const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(time.hours || 0),
        Number(time.minutes || 0),
        Number(time.seconds || 0)
    );

    return Number.isNaN(date.getTime()) ? null : date;
};

const parseStringDate = value => {
    const normalized = value.trim();

    const slashDate = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[,\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (slashDate) {
        return parseDateParts(slashDate[3], slashDate[2], slashDate[1], {
            hours: slashDate[4],
            minutes: slashDate[5],
            seconds: slashDate[6],
        });
    }

    const isoDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (isoDate) {
        return parseDateParts(isoDate[1], isoDate[2], isoDate[3], {
            hours: isoDate[4],
            minutes: isoDate[5],
            seconds: isoDate[6],
        });
    }

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toDate = value => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === "function") {
        const date = value.toDate();
        return Number.isNaN(date?.getTime?.()) ? null : date;
    }
    if (typeof value === "object" && Number.isFinite(value?.seconds)) {
        const date = new Date(value.seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === "object" && Number.isFinite(value?._seconds)) {
        const date = new Date(value._seconds * 1000);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === "string") return parseStringDate(value);

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatDayMonthYear = date => [
    padDatePart(date.getDate()),
    padDatePart(date.getMonth() + 1),
    date.getFullYear(),
].join("/");

export const formatInspectionDate = value => {
    const date = toDate(value);
    return date ? formatDayMonthYear(date) : "Not available";
};

export const formatInspectionDateTime = value => {
    const date = toDate(value);
    return date ? `${formatDayMonthYear(date)}, ${date.toLocaleTimeString("en-ZA")}` : "Not available";
};

export const formatInspectionTime = value => {
    const date = toDate(value);
    return date ? date.toLocaleTimeString("en-ZA") : "Not available";
};

export const normalizeInspectionChoiceValue = value => {
    if (value === true || value === false) return value;
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return value !== 0;

    const normalized = value.toString().trim().toLowerCase();
    if (["1", "true", "yes", "y", "good"].includes(normalized)) return true;
    if (["0", "false", "no", "n", "bad", "not good"].includes(normalized)) return false;

    return Boolean(normalized);
};

export const normalizeInspectionChoiceState = value => {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && value.trim() === "") return null;

    return normalizeInspectionChoiceValue(value);
};

export const normalizeInspectionRecord = (inspect, trip = null, fallbackInspection = null) => {
    const record = unwrapInspectionRecord(inspect);
    const fallback = unwrapInspectionRecord(fallbackInspection);
    if (!record) return record;
    const driverName = resolveInspectionDriverName(record, trip, fallback);

    return {
        ...fallback,
        ...record,
        driverName,
        refNumber: firstInspectionValue(record?.refNumber, record?.referenceNumber, fallback?.refNumber, fallback?.referenceNumber, getTripReference(trip)),
        regsitrationNumber: firstInspectionValue(record?.regsitrationNumber, record?.registrationNumber, record?.vehicleRegNumber, record?.VehicleRegNo, fallback?.regsitrationNumber, fallback?.registrationNumber, fallback?.vehicleRegNumber, fallback?.VehicleRegNo, trip?.vehicleRegNumber, trip?.VehicleRegNo),
        make: firstInspectionValue(record?.make, record?.vehicleMake, fallback?.make, fallback?.vehicleMake, trip?.make, trip?.vehicleMake),
        model: firstInspectionValue(record?.model, record?.vehicleModel, fallback?.model, fallback?.vehicleModel, trip?.model, trip?.vehicleModel, trip?.vehicleType, trip?.VehicleType),
        colour: firstInspectionValue(record?.colour, record?.color, fallback?.colour, fallback?.color, trip?.colour, trip?.color),
        dateOut: firstInspectionValue(record?.dateOut, record?.outDate, record?.date, record?.createdAt),
        kmsOut: firstInspectionValue(record?.kmsOut, record?.kmOut, record?.kilometersOut, record?.odometerReading, fallback?.kmsOut, fallback?.kmOut, fallback?.kilometersOut, fallback?.odometerReading),
        leftCommnet: firstInspectionValue(record?.leftCommnet, record?.leftComment),
        odometer: firstInspectionValue(record?.odometer, record?.odometerUrl, record?.odometerImage, record?.odometerPic),
        licenseDiskImage: firstInspectionValue(
            record?.licenseDiskImage,
            record?.licenceDiskImage,
            record?.licenseDiscImage,
            record?.licenceDiscImage,
            record?.licenseDisk,
            record?.licenceDisk,
            record?.licenseDisc,
            record?.licenceDisc,
            record?.licenseDiskPic,
            record?.licenceDiskPic,
            record?.diskImage,
            record?.licenseDiskUrl,
            record?.licenceDiskUrl,
            record?.licenseDiscUrl,
            record?.licenceDiscUrl,
            record?.diskUrl
        ),
        etagImage: firstInspectionValue(record?.etagImage, record?.eTagImage, record?.etagUrl, record?.eTagUrl, record?.etagImageUrl),
        driverSignature: firstInspectionValue(record?.driverSignature, record?.driverSignatureUrl),
        signature: firstInspectionValue(record?.signature, record?.clientSignatureUrl),
        leftPics: pickImageGroup(record, "leftPics", "LeftPics", "leftPic", "leftPicture", "leftPictures", "leftImages", "leftSide", "leftSidePic", "leftSidePics", "leftSidePicture", "leftSidePictures", "leftSideImages", "LeftSidePics"),
        rightPics: pickImageGroup(record, "rightPics", "RightPics", "rightPic", "rightPicture", "rightPictures", "rightImages", "rightSide", "rightSidePic", "rightSidePics", "rightSidePicture", "rightSidePictures", "rightSideImages", "RightSidePics"),
        frontPics: pickImageGroup(record, "frontPics", "FrontPics", "frontPic", "frontPicture", "frontPictures", "frontImages", "frontSide", "frontSidePic", "frontSidePics", "frontSidePicture", "frontSidePictures", "frontSideImages", "carFrontPics", "CarFrontPics"),
        backPics: pickImageGroup(record, "backPics", "BackPics", "backPic", "rearPic", "rearPics", "rearPictures", "backPicture", "backPictures", "backImages", "rearImages", "rearSidePics", "carRearPics", "CarRearPics"),
        leftFrontTirePics: pickImageGroup(record, "leftFrontTirePics", "LeftFrontTirePics", "leftFrontTyrePics", "leftFrontWheelPics", "leftFrontTirePic", "leftFrontTyrePic", "leftFrontWheelPic"),
        leftBackTirePics: pickImageGroup(record, "leftBackTirePics", "LeftBackTirePics", "leftBackTyrePics", "leftRearTirePics", "leftRearTyrePics", "leftBackWheelPics", "leftBackTirePic", "leftRearTirePic", "leftRearWheelPic"),
        rightFrontTirePics: pickImageGroup(record, "rightFrontTirePics", "RightFrontTirePics", "rightFrontTyrePics", "rightFrontWheelPics", "rightFrontTirePic", "rightFrontTyrePic", "rightFrontWheelPic"),
        rightBackTirePics: pickImageGroup(record, "rightBackTirePics", "RightBackTirePics", "rightBackTyrePics", "rightRearTirePics", "rightRearTyrePics", "rightBackWheelPics", "rightBackTirePic", "rightRearTirePic", "rightRearWheelPic"),
        windScreenPics: pickImageGroup(record, "windScreenPics", "WindScreenPics", "windscreenPics", "WindscreenPics", "windshieldPics", "windScreenPic", "windscreenPic", "windScreenPictures", "windscreenPictures", "windscreenImages"),
    };
};
